import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Dialog, ConfirmDialog } from './Dialog';
import { SidePanel } from './SidePanel';

// Modal types for type safety
type DialogSize = 'sm' | 'md' | 'lg' | 'xl';
type SidePanelWidth = 'narrow' | 'normal' | 'wide';
type ConfirmVariant = 'danger' | 'warning' | 'info';

interface DialogState {
  isOpen: boolean;
  title: string;
  content: ReactNode;
  size: DialogSize;
  showCloseButton: boolean;
  preventClickOutsideClose: boolean;
}

interface SidePanelState {
  isOpen: boolean;
  title: string;
  content: ReactNode;
  width: SidePanelWidth;
  showCloseButton: boolean;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  confirmText: string;
  cancelText: string;
  variant: ConfirmVariant;
}

interface ModalContextType {
  // Dialog methods
  openDialog: (
    content: ReactNode, 
    title: string, 
    options?: {
      size?: DialogSize;
      showCloseButton?: boolean;
      preventClickOutsideClose?: boolean;
    }
  ) => void;
  closeDialog: () => void;
  
  // Side panel methods
  openSidePanel: (
    content: ReactNode, 
    title: string, 
    options?: {
      width?: SidePanelWidth;
      showCloseButton?: boolean;
    }
  ) => void;
  closeSidePanel: () => void;
  
  // Confirmation dialog methods
  openConfirmDialog: (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmText?: string;
      cancelText?: string;
      variant?: ConfirmVariant;
    }
  ) => void;
  closeConfirmDialog: () => void;
  
  // Utility methods
  closeAll: () => void;
  isAnyModalOpen: () => boolean;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    title: '',
    content: null,
    size: 'md',
    showCloseButton: true,
    preventClickOutsideClose: false,
  });

  const [sidePanelState, setSidePanelState] = useState<SidePanelState>({
    isOpen: false,
    title: '',
    content: null,
    width: 'normal',
    showCloseButton: true,
  });

  const [confirmDialogState, setConfirmDialogState] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'info',
  });

  const openDialog = useCallback((
    content: ReactNode, 
    title: string, 
    options: {
      size?: DialogSize;
      showCloseButton?: boolean;
      preventClickOutsideClose?: boolean;
    } = {}
  ) => {
    // Close other modals first
    setSidePanelState(prev => ({ ...prev, isOpen: false }));
    setConfirmDialogState(prev => ({ ...prev, isOpen: false }));
    
    setDialogState({
      isOpen: true,
      title,
      content,
      size: options.size || 'md',
      showCloseButton: options.showCloseButton ?? true,
      preventClickOutsideClose: options.preventClickOutsideClose ?? false,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const openSidePanel = useCallback((
    content: ReactNode, 
    title: string, 
    options: {
      width?: SidePanelWidth;
      showCloseButton?: boolean;
    } = {}
  ) => {
    // Close other modals first
    setDialogState(prev => ({ ...prev, isOpen: false }));
    setConfirmDialogState(prev => ({ ...prev, isOpen: false }));
    
    setSidePanelState({
      isOpen: true,
      title,
      content,
      width: options.width || 'normal',
      showCloseButton: options.showCloseButton ?? true,
    });
  }, []);

  const closeSidePanel = useCallback(() => {
    setSidePanelState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const openConfirmDialog = useCallback((
    title: string,
    message: string,
    onConfirm: () => void,
    options: {
      confirmText?: string;
      cancelText?: string;
      variant?: ConfirmVariant;
    } = {}
  ) => {
    // Close other modals first
    setDialogState(prev => ({ ...prev, isOpen: false }));
    setSidePanelState(prev => ({ ...prev, isOpen: false }));
    
    setConfirmDialogState({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      variant: options.variant || 'info',
    });
  }, []);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialogState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const closeAll = useCallback(() => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
    setSidePanelState(prev => ({ ...prev, isOpen: false }));
    setConfirmDialogState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const isAnyModalOpen = useCallback(() => {
    return dialogState.isOpen || sidePanelState.isOpen || confirmDialogState.isOpen;
  }, [dialogState.isOpen, sidePanelState.isOpen, confirmDialogState.isOpen]);

  const contextValue: ModalContextType = {
    openDialog,
    closeDialog,
    openSidePanel,
    closeSidePanel,
    openConfirmDialog,
    closeConfirmDialog,
    closeAll,
    isAnyModalOpen,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      
      {/* Render active modals */}
      <Dialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        title={dialogState.title}
        size={dialogState.size}
        showCloseButton={dialogState.showCloseButton}
        preventClickOutsideClose={dialogState.preventClickOutsideClose}
      >
        {dialogState.content}
      </Dialog>

      <SidePanel
        isOpen={sidePanelState.isOpen}
        onClose={closeSidePanel}
        title={sidePanelState.title}
        width={sidePanelState.width}
        showCloseButton={sidePanelState.showCloseButton}
      >
        {sidePanelState.content}
      </SidePanel>

      <ConfirmDialog
        isOpen={confirmDialogState.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialogState.onConfirm}
        title={confirmDialogState.title}
        message={confirmDialogState.message}
        confirmText={confirmDialogState.confirmText}
        cancelText={confirmDialogState.cancelText}
        variant={confirmDialogState.variant}
      />
    </ModalContext.Provider>
  );
}

// Custom hook for using the modal context
export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

// Convenience hooks for specific modal types
export function useDialog() {
  const { openDialog, closeDialog } = useModal();
  return { openDialog, closeDialog };
}

export function useSidePanel() {
  const { openSidePanel, closeSidePanel } = useModal();
  return { openSidePanel, closeSidePanel };
}

export function useConfirmDialog() {
  const { openConfirmDialog, closeConfirmDialog } = useModal();
  return { openConfirmDialog, closeConfirmDialog };
}